import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useToast } from '@easymoving/ui';
import { useAuth } from '../../context/AuthContext';
import { LocalBackend } from '../../lib/backend/local';
import { migrateLocalToCloud } from '../../lib/backend/migrate';

/**
 * On first sign-in, if there's on-device guest data, migrate it to the user's
 * cloud account (blocking overlay), then it's cleared. Runs once per uid.
 */
export function MigrationGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const ranFor = useRef<string | null>(null);

  useEffect(() => {
    if (!user || ranFor.current === user.uid) return;
    ranFor.current = user.uid;
    let cancelled = false;
    void (async () => {
      const local = new LocalBackend();
      if (!(await local.hasData())) return;
      setProgress({ done: 0, total: 0 });
      try {
        await migrateLocalToCloud(user.uid, (done, total) => {
          if (!cancelled) setProgress({ done, total });
        });
        if (!cancelled) toast.push({ title: t('migrate.done'), tone: 'success' });
      } catch {
        if (!cancelled) toast.push({ title: t('migrate.failed'), tone: 'danger' });
      } finally {
        if (!cancelled) setProgress(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, t, toast]);

  return (
    <>
      {children}
      {progress && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-bg/90 backdrop-blur-sm"
          role="status"
          aria-live="polite"
        >
          <div className="surface p-8 text-center max-w-xs mx-4">
            <Loader2 className="size-8 text-petrol animate-spin mx-auto" />
            <h2 className="font-display text-lg tracking-snug mt-4">{t('migrate.title')}</h2>
            <p className="text-graphite text-[14px] mt-1">{t('migrate.subtitle')}</p>
            {progress.total > 0 && (
              <p className="text-graphite-soft text-[13px] tnum mt-2">
                {t('migrate.progress', { done: progress.done, total: progress.total })}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
